import { call, put, takeLatest } from 'redux-saga/effects';
import api from '../../helpers/api';
import {
  FETCH_CREATED_VS_RESOLVED,
  FETCH_CREATED_WORK_ORDERS,
  FETCH_RESOLVED_WORK_ORDERS,
  FETCH_WORK_ORDERS_BY_TYPE,
  FETCH_WORK_ORDERS_BY_TYPE_LIST,
  FETCH_REPEATING_REPORT,
  FETCH_REPEATING_REPORT_LIST,
  FETCH_STATUS_REPORT,
  FETCH_STATUS_REPORT_LIST,
  FETCH_PRIORITY_REPORT,
  FETCH_PRIORITY_REPORT_LIST,
  FETCH_TIME_TO_COMPLETE,
  FETCH_COMPLETED_WORK_ORDERS,
  FETCH_MTTR_WORK_ORDERS,
  FETCH_ON_TIME_OVERDUE,
  FETCH_ON_TIME_OVERDUE_LIST,
  FETCH_COMPLETED_BY_USER,
  FETCH_COMPLETED_BY_USER_LIST,
  FETCH_STATUS_DURATION_REPORT,
  FETCH_ASSET_PERFORMANCE,
  FETCH_RECENT_ACTIVITY,
  FETCH_RECENT_ACTIVITY_MORE,
  FETCH_CURRENT_STATUS,
  FETCH_MOST_PROBLEMATIC,
  FETCH_DOWNTIME_BREAKDOWN,
  FETCH_TOTAL_DOWNTIME,
  FETCH_DOWNTIME_COST,
  FETCH_WORK_ORDER_COST_REPORT,
  FETCH_MAINTENANCE_SPEND_BY_ASSET,
  EXPORT_CSV,
  EXPORT_GLOBAL_CSV,
} from './actionTypes';
import {
  fetchCreatedVsResolvedSuccess,
  fetchCreatedVsResolvedFail,
  fetchCreatedWorkOrdersSuccess,
  fetchCreatedWorkOrdersFail,
  fetchResolvedWorkOrdersSuccess,
  fetchResolvedWorkOrdersFail,
  fetchWorkOrdersByTypeSuccess,
  fetchWorkOrdersByTypeFail,
  fetchWorkOrdersByTypeListSuccess,
  fetchWorkOrdersByTypeListFail,
  fetchRepeatingReportSuccess,
  fetchRepeatingReportFail,
  fetchRepeatingReportListSuccess,
  fetchRepeatingReportListFail,
  fetchStatusReportSuccess,
  fetchStatusReportFail,
  fetchStatusReportListSuccess,
  fetchStatusReportListFail,
  fetchPriorityReportSuccess,
  fetchPriorityReportFail,
  fetchPriorityReportListSuccess,
  fetchPriorityReportListFail,
  fetchTimeToCompleteSuccess,
  fetchTimeToCompleteFail,
  fetchCompletedWorkOrdersSuccess,
  fetchCompletedWorkOrdersFail,
  fetchMttrWorkOrdersSuccess,
  fetchMttrWorkOrdersFail,
  fetchOnTimeOverdueSuccess,
  fetchOnTimeOverdueFail,
  fetchOnTimeOverdueListSuccess,
  fetchOnTimeOverdueListFail,
  fetchCompletedByUserSuccess,
  fetchCompletedByUserFail,
  fetchCompletedByUserListSuccess,
  fetchCompletedByUserListFail,
  fetchStatusDurationReportSuccess,
  fetchStatusDurationReportFail,
  fetchAssetPerformanceSuccess,
  fetchAssetPerformanceFail,
  fetchRecentActivitySuccess,
  fetchRecentActivityFail,
  fetchRecentActivityMoreSuccess,
  fetchRecentActivityMoreFail,
  fetchCurrentStatusSuccess,
  fetchCurrentStatusFail,
  fetchMostProblematicSuccess,
  fetchMostProblematicFail,
  fetchDowntimeBreakdownSuccess,
  fetchDowntimeBreakdownFail,
  fetchTotalDowntimeSuccess,
  fetchTotalDowntimeFail,
  fetchDowntimeCostSuccess,
  fetchDowntimeCostFail,
  fetchWorkOrderCostReportSuccess,
  fetchWorkOrderCostReportFail,
  fetchMaintenanceSpendByAssetSuccess,
  fetchMaintenanceSpendByAssetFail,
  exportCsvSuccess,
  exportCsvFail,
} from './actions';

/**
 * Appends non-null/non-empty reporting filter fields to a URLSearchParams instance.
 * Keys mirror the backend WorkOrderReportFilterRequest / ReportFilters shape.
 */
const appendFilters = (params, filters) => {
  if (!filters) return;
  const keys = ['assignedTo', 'priority', 'locationId', 'dueDateStart', 'dueDateEnd',
    'status', 'assetId', 'assetTypeId', 'criticality', 'vendorId',
    'contractNumber', 'workOrderCategoryId'];
  for (const k of keys) {
    const v = filters[k];
    if (v !== null && v !== undefined && v !== '') params.append(k, v);
  }
};

/**
 * Fetch Created vs Resolved Report
 */
function* fetchCreatedVsResolvedSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, filters } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    appendFilters(params, filters);

    const { data } = yield call(api.get, `reporting/work-orders/created-vs-resolved?${params.toString()}`);
    yield put(fetchCreatedVsResolvedSuccess(data.response));
  } catch (error) {
    console.error('Error fetching Created vs Resolved report:', error);
    yield put(fetchCreatedVsResolvedFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

/**
 * Fetch Created Work Orders List
 */
function* fetchCreatedWorkOrdersSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, periodStart, periodEnd, periodLabel } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    if (periodStart) params.append('periodStart', periodStart);
    if (periodEnd) params.append('periodEnd', periodEnd);
    if (periodLabel) params.append('periodLabel', periodLabel);

    const { data } = yield call(api.get, `reporting/work-orders/created?${params.toString()}`);
    yield put(fetchCreatedWorkOrdersSuccess(data.response));
  } catch (error) {
    console.error('Error fetching created work orders:', error);
    yield put(fetchCreatedWorkOrdersFail(error.response?.data?.message || 'Failed to fetch work orders'));
  }
}

/**
 * Fetch Resolved Work Orders List
 */
function* fetchResolvedWorkOrdersSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, periodStart, periodEnd, periodLabel } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    if (periodStart) params.append('periodStart', periodStart);
    if (periodEnd) params.append('periodEnd', periodEnd);
    if (periodLabel) params.append('periodLabel', periodLabel);

    const { data } = yield call(api.get, `reporting/work-orders/resolved?${params.toString()}`);
    yield put(fetchResolvedWorkOrdersSuccess(data.response));
  } catch (error) {
    console.error('Error fetching resolved work orders:', error);
    yield put(fetchResolvedWorkOrdersFail(error.response?.data?.message || 'Failed to fetch work orders'));
  }
}

/**
 * Fetch Work Orders by Type Report
 */
function* fetchWorkOrdersByTypeSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, filters } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    appendFilters(params, filters);

    const { data } = yield call(api.get, `reporting/work-orders/by-type?${params.toString()}`);
    yield put(fetchWorkOrdersByTypeSuccess(data.response));
  } catch (error) {
    console.error('Error fetching Work Orders by Type report:', error);
    yield put(fetchWorkOrdersByTypeFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

/**
 * Fetch Work Orders by Type List
 */
function* fetchWorkOrdersByTypeListSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, workType, periodStart, periodEnd, periodLabel } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    params.append('workType', workType);
    if (periodStart) params.append('periodStart', periodStart);
    if (periodEnd) params.append('periodEnd', periodEnd);
    if (periodLabel) params.append('periodLabel', periodLabel);

    const { data } = yield call(api.get, `reporting/work-orders/by-type/list?${params.toString()}`);
    yield put(fetchWorkOrdersByTypeListSuccess(data.response));
  } catch (error) {
    console.error('Error fetching work orders by type list:', error);
    yield put(fetchWorkOrdersByTypeListFail(error.response?.data?.message || 'Failed to fetch work orders'));
  }
}

/**
 * Fetch Repeating Report
 */
function* fetchRepeatingReportSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, filters } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    appendFilters(params, filters);

    const { data } = yield call(api.get, `reporting/work-orders/repeating?${params.toString()}`);
    yield put(fetchRepeatingReportSuccess(data.response));
  } catch (error) {
    console.error('Error fetching Repeating report:', error);
    yield put(fetchRepeatingReportFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

/**
 * Fetch Repeating Report List
 */
function* fetchRepeatingReportListSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, isRepeating, periodStart, periodEnd, periodLabel } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    params.append('isRepeating', isRepeating);
    if (periodStart) params.append('periodStart', periodStart);
    if (periodEnd) params.append('periodEnd', periodEnd);
    if (periodLabel) params.append('periodLabel', periodLabel);

    const { data } = yield call(api.get, `reporting/work-orders/repeating/list?${params.toString()}`);
    yield put(fetchRepeatingReportListSuccess(data.response));
  } catch (error) {
    console.error('Error fetching repeating work orders list:', error);
    yield put(fetchRepeatingReportListFail(error.response?.data?.message || 'Failed to fetch work orders'));
  }
}

/**
 * Fetch Status Report
 */
function* fetchStatusReportSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, filters } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    appendFilters(params, filters);

    const { data } = yield call(api.get, `reporting/work-orders/by-status?${params.toString()}`);
    yield put(fetchStatusReportSuccess(data.response));
  } catch (error) {
    console.error('Error fetching Status report:', error);
    yield put(fetchStatusReportFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

/**
 * Fetch Status Report List
 */
function* fetchStatusReportListSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, status } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    params.append('status', status);

    const { data } = yield call(api.get, `reporting/work-orders/by-status/list?${params.toString()}`);
    yield put(fetchStatusReportListSuccess(data.response));
  } catch (error) {
    console.error('Error fetching work orders by status list:', error);
    yield put(fetchStatusReportListFail(error.response?.data?.message || 'Failed to fetch work orders'));
  }
}

/**
 * Fetch Priority Report
 */
function* fetchPriorityReportSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, filters } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    appendFilters(params, filters);

    const { data } = yield call(api.get, `reporting/work-orders/by-priority?${params.toString()}`);
    yield put(fetchPriorityReportSuccess(data.response));
  } catch (error) {
    console.error('Error fetching Priority report:', error);
    yield put(fetchPriorityReportFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

/**
 * Fetch Priority Report List
 */
function* fetchPriorityReportListSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, priority } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    params.append('priority', priority);

    const { data } = yield call(api.get, `reporting/work-orders/by-priority/list?${params.toString()}`);
    yield put(fetchPriorityReportListSuccess(data.response));
  } catch (error) {
    console.error('Error fetching work orders by priority list:', error);
    yield put(fetchPriorityReportListFail(error.response?.data?.message || 'Failed to fetch work orders'));
  }
}

/**
 * Fetch Time to Complete Report
 */
function* fetchTimeToCompleteSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, filters } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    appendFilters(params, filters);

    const { data } = yield call(api.get, `reporting/work-orders/time-to-complete?${params.toString()}`);
    yield put(fetchTimeToCompleteSuccess(data.response));
  } catch (error) {
    console.error('Error fetching Time to Complete report:', error);
    yield put(fetchTimeToCompleteFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

/**
 * Fetch Completed Work Orders List
 */
function* fetchCompletedWorkOrdersSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, periodStart, periodEnd, periodLabel } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    if (periodStart) params.append('periodStart', periodStart);
    if (periodEnd) params.append('periodEnd', periodEnd);
    if (periodLabel) params.append('periodLabel', periodLabel);

    const { data } = yield call(api.get, `reporting/work-orders/time-to-complete/list?${params.toString()}`);
    yield put(fetchCompletedWorkOrdersSuccess(data.response));
  } catch (error) {
    console.error('Error fetching completed work orders:', error);
    yield put(fetchCompletedWorkOrdersFail(error.response?.data?.message || 'Failed to fetch work orders'));
  }
}

/**
 * Fetch MTTR Work Orders List
 */
function* fetchMttrWorkOrdersSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, periodStart, periodEnd, periodLabel } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    if (periodStart) params.append('periodStart', periodStart);
    if (periodEnd) params.append('periodEnd', periodEnd);
    if (periodLabel) params.append('periodLabel', periodLabel);

    const { data } = yield call(api.get, `reporting/work-orders/time-to-complete/mttr/list?${params.toString()}`);
    yield put(fetchMttrWorkOrdersSuccess(data.response));
  } catch (error) {
    console.error('Error fetching MTTR work orders:', error);
    yield put(fetchMttrWorkOrdersFail(error.response?.data?.message || 'Failed to fetch work orders'));
  }
}

/**
 * Fetch On Time vs Overdue Report
 */
function* fetchOnTimeOverdueSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, filters } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    appendFilters(params, filters);

    const { data } = yield call(api.get, `reporting/work-orders/on-time-overdue?${params.toString()}`);
    yield put(fetchOnTimeOverdueSuccess(data.response));
  } catch (error) {
    console.error('Error fetching On Time vs Overdue report:', error);
    yield put(fetchOnTimeOverdueFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

/**
 * Fetch On Time vs Overdue List
 */
function* fetchOnTimeOverdueListSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, overdueStatus, isRepeating } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    params.append('overdueStatus', overdueStatus);
    if (isRepeating != null) {
      params.append('isRepeating', isRepeating);
    }

    const { data } = yield call(api.get, `reporting/work-orders/on-time-overdue/list?${params.toString()}`);
    yield put(fetchOnTimeOverdueListSuccess(data.response));
  } catch (error) {
    console.error('Error fetching on-time-overdue list:', error);
    yield put(fetchOnTimeOverdueListFail(error.response?.data?.message || 'Failed to fetch work orders'));
  }
}

/**
 * Fetch Completed by User Report
 */
function* fetchCompletedByUserSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, filters } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    appendFilters(params, filters);

    const { data } = yield call(api.get, `reporting/work-orders/completed-by-user?${params.toString()}`);
    yield put(fetchCompletedByUserSuccess(data.response));
  } catch (error) {
    console.error('Error fetching Completed by User report:', error);
    yield put(fetchCompletedByUserFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

/**
 * Fetch Completed by User List
 */
function* fetchCompletedByUserListSaga({ payload }) {
  try {
    const { organizationId, userId, weekStart, weekEnd } = payload;
    const params = new URLSearchParams();
    params.append('organizationId', organizationId);
    params.append('userId', userId);
    params.append('weekStart', weekStart);
    params.append('weekEnd', weekEnd);

    const { data } = yield call(api.get, `reporting/work-orders/completed-by-user/list?${params.toString()}`);
    yield put(fetchCompletedByUserListSuccess(data.response));
  } catch (error) {
    console.error('Error fetching completed-by-user list:', error);
    yield put(fetchCompletedByUserListFail(error.response?.data?.message || 'Failed to fetch work orders'));
  }
}

/**
 * Fetch Status Duration Report
 */
function* fetchStatusDurationReportSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, filters } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    appendFilters(params, filters);

    const { data } = yield call(api.get, `reporting/work-orders/status-durations?${params.toString()}`);
    yield put(fetchStatusDurationReportSuccess(data.response));
  } catch (error) {
    console.error('Error fetching Status Duration report:', error);
    yield put(fetchStatusDurationReportFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

/**
 * Fetch Asset Performance Report
 */
function* fetchAssetPerformanceSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);

    const { data } = yield call(api.get, `reporting/work-orders/asset-performance?${params.toString()}`);
    yield put(fetchAssetPerformanceSuccess(data.response));
  } catch (error) {
    console.error('Error fetching Asset Performance report:', error);
    yield put(fetchAssetPerformanceFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

/**
 * Build URLSearchParams for recent activity endpoint
 */
function buildRecentActivityParams(payload) {
  const params = new URLSearchParams();
  params.append('startDate', payload.startDate);
  params.append('endDate', payload.endDate);
  params.append('organizationId', payload.organizationId);
  if (payload.assignedTo) params.append('assignedTo', payload.assignedTo);
  if (payload.locationId) params.append('locationId', payload.locationId);
  if (payload.priority) params.append('priority', payload.priority);
  if (payload.dueDateStart) params.append('dueDateStart', payload.dueDateStart);
  if (payload.dueDateEnd) params.append('dueDateEnd', payload.dueDateEnd);
  params.append('page', payload.page || 0);
  params.append('pageSize', payload.pageSize || 15);
  return params;
}

/**
 * Fetch Recent Activity Report
 */
function* fetchRecentActivitySaga({ payload }) {
  try {
    const params = buildRecentActivityParams(payload);
    const { data } = yield call(api.get, `reporting/work-orders/recent-activity?${params.toString()}`);
    yield put(fetchRecentActivitySuccess(data.response));
  } catch (error) {
    console.error('Error fetching Recent Activity report:', error);
    yield put(fetchRecentActivityFail(error.response?.data?.message || 'Failed to fetch recent activity'));
  }
}

/**
 * Fetch Recent Activity More (Load More / infinite scroll)
 */
function* fetchRecentActivityMoreSaga({ payload }) {
  try {
    const params = buildRecentActivityParams(payload);
    const { data } = yield call(api.get, `reporting/work-orders/recent-activity?${params.toString()}`);
    yield put(fetchRecentActivityMoreSuccess(data.response));
  } catch (error) {
    console.error('Error fetching more recent activity:', error);
    yield put(fetchRecentActivityMoreFail(error.response?.data?.message || 'Failed to load more activity'));
  }
}

/**
 * Export CSV — downloads a CSV blob from the server
 */
function* exportCsvSaga({ payload }) {
  try {
    const { exportType, organizationId, startDate, endDate } = payload;
    const params = new URLSearchParams();
    params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = yield call(api.get, `csv-reports/${exportType}?${params.toString()}`, {
      responseType: 'blob',
    });

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `${exportType}_export.csv`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, '');
      }
    }

    // Create blob and trigger download
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    yield put(exportCsvSuccess());
  } catch (error) {
    console.error('Error exporting CSV:', error);

    // When responseType is 'blob', error response body is also a Blob — read as text
    let errorMessage = 'Failed to export data';
    if (error.response?.data instanceof Blob) {
      try {
        const text = yield call([error.response.data, 'text']);
        const parsed = JSON.parse(text);
        errorMessage = parsed.message || parsed.error || errorMessage;
      } catch {
        // ignore parse errors
      }
    } else {
      errorMessage = error.response?.data?.message || errorMessage;
    }

    yield put(exportCsvFail(errorMessage));
  }
}

function* exportGlobalCsvSaga({ payload }) {
  try {
    const { exportType, startDate, endDate } = payload;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const query = params.toString();
    const response = yield call(
      api.get,
      `csv-reports/global/${exportType}${query ? `?${query}` : ''}`,
      { responseType: 'blob' }
    );

    const contentDisposition = response.headers['content-disposition'];
    let filename = `global_${exportType}_export.csv`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, '');
      }
    }

    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    yield put(exportCsvSuccess());
  } catch (error) {
    console.error('Error exporting global CSV:', error);

    let errorMessage = 'Failed to export data';
    if (error.response?.data instanceof Blob) {
      try {
        const text = yield call([error.response.data, 'text']);
        const parsed = JSON.parse(text);
        errorMessage = parsed.message || parsed.error || errorMessage;
      } catch {
        // ignore parse errors
      }
    } else {
      errorMessage = error.response?.data?.message || errorMessage;
    }

    yield put(exportCsvFail(errorMessage));
  }
}

/**
 * Fetch Current Status Report (Asset Health)
 */
function* fetchCurrentStatusSaga({ payload }) {
  try {
    const { organizationId, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber } = payload;
    const params = new URLSearchParams();
    params.append('organizationId', organizationId);
    if (criticality) params.append('criticality', criticality);
    if (locationId) params.append('locationId', locationId);
    if (vendorId) params.append('vendorId', vendorId);
    if (assetId) params.append('assetId', assetId);
    if (assetTypeId) params.append('assetTypeId', assetTypeId);
    if (contractNumber) params.append('contractNumber', contractNumber);

    const { data } = yield call(api.get, `reporting/assets/current-status?${params.toString()}`);
    yield put(fetchCurrentStatusSuccess(data.response));
  } catch (error) {
    console.error('Error fetching Current Status report:', error);
    yield put(fetchCurrentStatusFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

/**
 * Fetch Most Problematic Assets Report (Asset Health)
 */
function* fetchMostProblematicSaga({ payload }) {
  try {
    const { organizationId, startDate, endDate, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber } = payload;
    const params = new URLSearchParams();
    params.append('organizationId', organizationId);
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    if (criticality) params.append('criticality', criticality);
    if (locationId) params.append('locationId', locationId);
    if (vendorId) params.append('vendorId', vendorId);
    if (assetId) params.append('assetId', assetId);
    if (assetTypeId) params.append('assetTypeId', assetTypeId);
    if (contractNumber) params.append('contractNumber', contractNumber);

    const { data } = yield call(api.get, `reporting/assets/most-problematic?${params.toString()}`);
    yield put(fetchMostProblematicSuccess(data.response));
  } catch (error) {
    console.error('Error fetching Most Problematic Assets report:', error);
    yield put(fetchMostProblematicFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

/**
 * Fetch Downtime Breakdown Report (Asset Health)
 */
function* fetchDowntimeBreakdownSaga({ payload }) {
  try {
    const { organizationId, startDate, endDate, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber } = payload;
    const params = new URLSearchParams();
    params.append('organizationId', organizationId);
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    if (criticality) params.append('criticality', criticality);
    if (locationId) params.append('locationId', locationId);
    if (vendorId) params.append('vendorId', vendorId);
    if (assetId) params.append('assetId', assetId);
    if (assetTypeId) params.append('assetTypeId', assetTypeId);
    if (contractNumber) params.append('contractNumber', contractNumber);

    const { data } = yield call(api.get, `reporting/assets/downtime-breakdown?${params.toString()}`);
    yield put(fetchDowntimeBreakdownSuccess(data.response));
  } catch (error) {
    console.error('Error fetching Downtime Breakdown report:', error);
    yield put(fetchDowntimeBreakdownFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

/**
 * Fetch Total Downtime Report (Asset Health)
 */
function* fetchTotalDowntimeSaga({ payload }) {
  try {
    const { organizationId, startDate, endDate, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber } = payload;
    const params = new URLSearchParams();
    params.append('organizationId', organizationId);
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    if (criticality) params.append('criticality', criticality);
    if (locationId) params.append('locationId', locationId);
    if (vendorId) params.append('vendorId', vendorId);
    if (assetId) params.append('assetId', assetId);
    if (assetTypeId) params.append('assetTypeId', assetTypeId);
    if (contractNumber) params.append('contractNumber', contractNumber);

    const { data } = yield call(api.get, `reporting/assets/total-downtime?${params.toString()}`);
    yield put(fetchTotalDowntimeSuccess(data.response));
  } catch (error) {
    console.error('Error fetching Total Downtime report:', error);
    yield put(fetchTotalDowntimeFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

/**
 * Fetch Work Order Cost Report (labor / parts / contractor)
 */
function* fetchWorkOrderCostReportSaga({ payload }) {
  try {
    const { startDate, endDate, organizationId, filters } = payload;
    const params = new URLSearchParams();
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('organizationId', organizationId);
    appendFilters(params, filters);

    const { data } = yield call(api.get, `reporting/work-orders/cost?${params.toString()}`);
    yield put(fetchWorkOrderCostReportSuccess(data.response));
  } catch (error) {
    console.error('Error fetching Work Order Cost report:', error);
    yield put(fetchWorkOrderCostReportFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

/**
 * Fetch Downtime Cost Report (Asset Health)
 */
function* fetchDowntimeCostSaga({ payload }) {
  try {
    const { organizationId, startDate, endDate, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber } = payload;
    const params = new URLSearchParams();
    params.append('organizationId', organizationId);
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    if (criticality) params.append('criticality', criticality);
    if (locationId) params.append('locationId', locationId);
    if (vendorId) params.append('vendorId', vendorId);
    if (assetId) params.append('assetId', assetId);
    if (assetTypeId) params.append('assetTypeId', assetTypeId);
    if (contractNumber) params.append('contractNumber', contractNumber);

    const { data } = yield call(api.get, `reporting/assets/downtime-cost?${params.toString()}`);
    yield put(fetchDowntimeCostSuccess(data.response));
  } catch (error) {
    console.error('Error fetching Downtime Cost report:', error);
    yield put(fetchDowntimeCostFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

/**
 * Fetch Maintenance Spend by Asset Report (Asset Health)
 */
function* fetchMaintenanceSpendByAssetSaga({ payload }) {
  try {
    const {
      organizationId, startDate, endDate,
      criticality, locationId, vendorId, assetId, assetTypeId, contractNumber,
      limit, offset, sortBy,
    } = payload;
    const params = new URLSearchParams();
    params.append('organizationId', organizationId);
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    if (criticality) params.append('criticality', criticality);
    if (locationId) params.append('locationId', locationId);
    if (vendorId) params.append('vendorId', vendorId);
    if (assetId) params.append('assetId', assetId);
    if (assetTypeId) params.append('assetTypeId', assetTypeId);
    if (contractNumber) params.append('contractNumber', contractNumber);
    if (limit != null) params.append('limit', limit);
    if (offset != null) params.append('offset', offset);
    if (sortBy) params.append('sortBy', sortBy);

    const { data } = yield call(api.get, `reporting/assets/maintenance-spend?${params.toString()}`);
    yield put(fetchMaintenanceSpendByAssetSuccess(data.response));
  } catch (error) {
    console.error('Error fetching Maintenance Spend by Asset report:', error);
    yield put(fetchMaintenanceSpendByAssetFail(error.response?.data?.message || 'Failed to fetch report'));
  }
}

function* reportingSaga() {
  yield takeLatest(FETCH_CREATED_VS_RESOLVED, fetchCreatedVsResolvedSaga);
  yield takeLatest(FETCH_CREATED_WORK_ORDERS, fetchCreatedWorkOrdersSaga);
  yield takeLatest(FETCH_RESOLVED_WORK_ORDERS, fetchResolvedWorkOrdersSaga);
  yield takeLatest(FETCH_WORK_ORDERS_BY_TYPE, fetchWorkOrdersByTypeSaga);
  yield takeLatest(FETCH_WORK_ORDERS_BY_TYPE_LIST, fetchWorkOrdersByTypeListSaga);
  yield takeLatest(FETCH_REPEATING_REPORT, fetchRepeatingReportSaga);
  yield takeLatest(FETCH_REPEATING_REPORT_LIST, fetchRepeatingReportListSaga);
  yield takeLatest(FETCH_STATUS_REPORT, fetchStatusReportSaga);
  yield takeLatest(FETCH_STATUS_REPORT_LIST, fetchStatusReportListSaga);
  yield takeLatest(FETCH_PRIORITY_REPORT, fetchPriorityReportSaga);
  yield takeLatest(FETCH_PRIORITY_REPORT_LIST, fetchPriorityReportListSaga);
  yield takeLatest(FETCH_TIME_TO_COMPLETE, fetchTimeToCompleteSaga);
  yield takeLatest(FETCH_COMPLETED_WORK_ORDERS, fetchCompletedWorkOrdersSaga);
  yield takeLatest(FETCH_MTTR_WORK_ORDERS, fetchMttrWorkOrdersSaga);
  yield takeLatest(FETCH_ON_TIME_OVERDUE, fetchOnTimeOverdueSaga);
  yield takeLatest(FETCH_ON_TIME_OVERDUE_LIST, fetchOnTimeOverdueListSaga);
  yield takeLatest(FETCH_COMPLETED_BY_USER, fetchCompletedByUserSaga);
  yield takeLatest(FETCH_COMPLETED_BY_USER_LIST, fetchCompletedByUserListSaga);
  yield takeLatest(FETCH_STATUS_DURATION_REPORT, fetchStatusDurationReportSaga);
  yield takeLatest(FETCH_ASSET_PERFORMANCE, fetchAssetPerformanceSaga);
  yield takeLatest(FETCH_RECENT_ACTIVITY, fetchRecentActivitySaga);
  yield takeLatest(FETCH_RECENT_ACTIVITY_MORE, fetchRecentActivityMoreSaga);
  yield takeLatest(FETCH_CURRENT_STATUS, fetchCurrentStatusSaga);
  yield takeLatest(FETCH_MOST_PROBLEMATIC, fetchMostProblematicSaga);
  yield takeLatest(FETCH_DOWNTIME_BREAKDOWN, fetchDowntimeBreakdownSaga);
  yield takeLatest(FETCH_TOTAL_DOWNTIME, fetchTotalDowntimeSaga);
  yield takeLatest(FETCH_DOWNTIME_COST, fetchDowntimeCostSaga);
  yield takeLatest(FETCH_WORK_ORDER_COST_REPORT, fetchWorkOrderCostReportSaga);
  yield takeLatest(FETCH_MAINTENANCE_SPEND_BY_ASSET, fetchMaintenanceSpendByAssetSaga);
  yield takeLatest(EXPORT_CSV, exportCsvSaga);
  yield takeLatest(EXPORT_GLOBAL_CSV, exportGlobalCsvSaga);
}

export default reportingSaga;
